import React from 'react';
import { SUPPORTED_LANGUAGES, useLanguage, type Language } from '../i18n';

/**
 * Sits in the header of every page. A native select rather than a bespoke
 * menu: it is keyboard-navigable and screen-reader-labelled without any work,
 * it uses the platform's own picker on a phone, and it grows to a third
 * language without a redesign.
 *
 * Each language is named in itself — a visitor looking for German is looking
 * for the word "Deutsch", whatever the page currently speaks.
 */

/**
 * Decoration that makes the picker scannable, never the label. A flag is a
 * country and a language is not: 🇩🇪 is wrong for the Austrian and Swiss
 * readers `de` also serves, and 🇬🇧 vs 🇺🇸 is the same argument for English. So
 * the endonym stays beside it and does the actual work — drop these and the
 * picker still says everything it needs to.
 *
 * Here rather than in the catalogue because it is the same glyph whatever
 * language the page is in. Adding a language stays one catalogue file plus one
 * line here.
 */
const flagFor: Record<Language, string> = {
  en: '🇬🇧',
  de: '🇩🇪',
};
export const LanguagePicker: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { language, setLanguage, t } = useLanguage();

  return (
    <select
      value={language}
      aria-label={t('common.language.label')}
      onChange={(event) => setLanguage(event.target.value as Language)}
      className={[
        'bg-transparent text-text-secondary hover:text-text-primary text-label font-sans font-medium',
        'rounded-full border border-gray-200 px-2 py-1 cursor-pointer transition-base',
        'focus:outline-none focus:ring-2 focus:ring-brand-secondary',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {SUPPORTED_LANGUAGES.map((code) => (
        <option key={code} value={code}>
          {`${flagFor[code]} ${t(`common.language.${code}`)}`}
        </option>
      ))}
    </select>
  );
};
